import React, { useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  useMediaQuery,
  alpha,
} from "@mui/material";
import { ArrowForwardRounded, CheckRounded } from "@mui/icons-material";
import { useCertifications } from "../../Query/useCertifications";
import { useQuestionListsForEvaluation } from "../../Query/useQuestions";
import colors from "../../Constants/colors";
import { Steps } from "../../Enums/companyStatus";
import { useSnackbar } from "notistack";
import theme from "../../Constants/theme";
import { useTranslation } from "react-i18next";

interface CategoryAndQuestionFormProps {
  certificationId: string;
  setCertificationId: React.Dispatch<React.SetStateAction<string>>;
  questionListId: string;
  setQuestionListId: React.Dispatch<React.SetStateAction<string>>;
  isEvaluateLoading: boolean;
  selectedModelId: number | "";
  currentStep: number;
  questionListStepIndex: number;
  onCategorySelected?: () => void;
  onQuestionListSelected?: () => void;
}

const CategoryAndQuestionForm: React.FC<CategoryAndQuestionFormProps> = ({
  certificationId,
  setCertificationId,
  questionListId,
  setQuestionListId,
  currentStep,
  questionListStepIndex,
  onCategorySelected,
  onQuestionListSelected,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const {
    data: certifications,
    isLoading: isCertLoading,
    isError: isCertError,
    error: certError,
  } = useCertifications();

  const {
    data: questionLists,
    isLoading: isQLoading,
    isError: isQLError,
    error: qListError,
    refetch,
  } = useQuestionListsForEvaluation(
    certificationId ? Number(certificationId) : undefined,
  );

  const isAnyError = isCertError || isQLError;
  const isLoadingCert =
    isCertLoading && currentStep === Steps.SELECT_CERTFICATION_CATEGORY;
  const isLoadingQL =
    isQLoading &&
    questionListStepIndex !== -1 &&
    currentStep === questionListStepIndex;
  const { t } = useTranslation("evaluationPage");

  useEffect(() => {
    if (
      certificationId &&
      questionListStepIndex !== -1 &&
      currentStep === questionListStepIndex
    ) {
      setQuestionListId("");
      refetch();
    }
  }, [
    certificationId,
    setQuestionListId,
    refetch,
    currentStep,
    questionListStepIndex,
  ]);

  useEffect(() => {
    if (
      currentStep === Steps.SELECT_CERTFICATION_CATEGORY &&
      certifications?.length === 1
    ) {
      setCertificationId(certifications[0].id.toString());
    }
  }, [certifications, currentStep, setCertificationId]);

  useEffect(() => {
    if (questionLists?.question_lists?.length) {
      if (questionLists.question_lists.length === 1) {
        setQuestionListId(questionLists.question_lists[0].id.toString());
      } else {
        const defaultQuestionList = questionLists.question_lists.find(
          (list) => list.default,
        );
        if (defaultQuestionList) {
          setQuestionListId(defaultQuestionList.id.toString());
        }
      }
    }
  }, [questionLists, currentStep, setQuestionListId, questionListStepIndex]);

  if (isLoadingCert) {
    return (
      <Box sx={{ width: "100%", pt: 2, pb: 2 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            py: 8,
          }}
        >
          <CircularProgress sx={{ color: colors.primary_dark }} />
        </Box>
      </Box>
    );
  }

  if (isLoadingQL) {
    return (
      <Box sx={{ width: "100%", pt: 2, pb: 2 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            py: 8,
          }}
        >
          <CircularProgress sx={{ color: colors.primary_dark }} />
        </Box>
      </Box>
    );
  }

  if (isAnyError) {
    const errorMessage =
      (certError?.message || qListError?.message) ?? "Error loading data.";
    enqueueSnackbar(errorMessage, { variant: "error" });
    return (
      <Box sx={{ width: "100%", pt: 2, pb: 2 }}>
        <Paper
          elevation={0}
          sx={{
            p: 3,
            borderRadius: 2,
            border: `1px solid ${colors.red200}`,
            borderLeft: `4px solid ${colors.red600}`,
          }}
        >
          <Typography sx={{ color: colors.red700 }}>{errorMessage}</Typography>
        </Paper>
      </Box>
    );
  }

  const handleCertificationClick = (id: string) => {
    setCertificationId(id);
    setQuestionListId("");
    onCategorySelected?.();
  };

  const handleQuestionListClick = (id: string) => {
    setQuestionListId(id);
    onQuestionListSelected?.();
  };

  const certGrid = (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: {
          xs: "1fr",
          sm: "1fr",
          md: "repeat(2, 1fr)",
        },
        gap: 2,
        mt: 1,
      }}
    >
      {certifications?.map((cert) => {
        const isSelected = certificationId === cert.id.toString();
        return (
          <Paper
            key={cert.id}
            elevation={0}
            sx={{
              width: "100%",
              maxWidth: "100%",
              overflow: "hidden",
              p: 2.5,
              borderRadius: 2,
              border: `2px solid ${
                isSelected ? colors.accent : colors.gray200
              }`,
              borderLeft: `4px solid ${
                isSelected ? colors.accent : colors.primary_dark
              }`,
              cursor: "pointer",
              backgroundColor: isSelected ? colors.orange50 : colors.white,
              transition: "all 0.2s ease-in-out",
              "&:hover": {
                borderColor: colors.accent,
                borderLeftColor: colors.accent,
                backgroundColor: isSelected
                  ? alpha(colors.accent, 0.7)
                  : alpha(colors.accent, 0.2),
              },
            }}
            onClick={() => handleCertificationClick(cert.id.toString())}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1.5,
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 600,
                  color: colors.primary_dark,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  flex: 1,
                  minWidth: 0,
                }}
              >
                {cert.name}
              </Typography>
              <Box sx={{ flexShrink: 0 }}>
                {isSelected ? (
                  <CheckRounded sx={{ fontSize: 24, color: colors.accent }} />
                ) : (
                  <ArrowForwardRounded
                    sx={{ fontSize: 20, color: colors.gray400 }}
                  />
                )}
              </Box>
            </Box>
          </Paper>
        );
      })}
    </Box>
  );

  const questionListGrid = (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: {
          xs: "1fr",
          sm: "1fr",
          md: "repeat(2, 1fr)",
        },
        gap: 2,
        mt: 1,
      }}
    >
      {questionLists?.question_lists.map((list) => {
        const isSelected = questionListId === list.id.toString();
        return (
          <Paper
            key={list.id}
            elevation={0}
            sx={{
              p: 2.5,
              borderRadius: 2,
              border: `2px solid ${
                isSelected ? colors.accent : colors.gray200
              }`,
              borderLeft: `4px solid ${
                isSelected ? colors.accent : colors.primary_dark
              }`,
              cursor: "pointer",
              backgroundColor: isSelected ? colors.orange50 : colors.white,
              transition: "all 0.2s ease-in-out",
              "&:hover": {
                borderColor: colors.accent,
                borderLeftColor: colors.accent,
                backgroundColor: isSelected
                  ? alpha(colors.accent, 0.7)
                  : alpha(colors.accent, 0.2),
              },
            }}
            onClick={() => handleQuestionListClick(list.id.toString())}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "space-between",
                gap: 1.5,
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 600,
                    color: colors.primary_dark,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    mb: 0.25,
                  }}
                >
                  {list.name}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: colors.gray600,
                    fontSize: "0.8rem",
                  }}
                >
                  {t("evaluationPage.createdBy")}
                  {list.default
                    ? "System"
                    : (list.created_by?.name ?? "System")}
                </Typography>
              </Box>
              <Box sx={{ flexShrink: 0 }}>
                {isSelected ? (
                  <CheckRounded sx={{ fontSize: 24, color: colors.accent }} />
                ) : (
                  <ArrowForwardRounded
                    sx={{ fontSize: 20, color: colors.gray400 }}
                  />
                )}
              </Box>
            </Box>
          </Paper>
        );
      })}
    </Box>
  );

  return (
    <Box sx={{ width: "100%", pt: 2, pb: 2 }}>
      {currentStep === Steps.SELECT_CERTFICATION_CATEGORY && (
        <>
          <Paper
            elevation={0}
            sx={{
              p: 2.5,
              mb: 2,
              borderRadius: 2,
              border: `1px solid ${colors.gray200}`,
              borderLeft: `4px solid ${colors.primary_dark}`,
            }}
          >
            <Typography
              variant={isMobile ? "h5" : "h4"}
              sx={{
                fontWeight: 600,
                color: colors.primary_dark,
                marginBottom: 0,
              }}
            >
              {t("evaluationPage.selectCertificationCategory")}
            </Typography>
          </Paper>
          {certGrid}
        </>
      )}

      {questionListStepIndex !== -1 &&
        currentStep === questionListStepIndex && (
          <>
            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                mb: 2,
                borderRadius: 2,
                border: `1px solid ${colors.gray200}`,
                borderLeft: `4px solid ${colors.primary_dark}`,
              }}
            >
              <Typography
                variant={isMobile ? "h5" : "h4"}
                sx={{
                  fontWeight: 600,
                  color: colors.primary_dark,
                  marginBottom: 0,
                }}
              >
                {t("evaluationPage.selectQuestionList")}
              </Typography>
            </Paper>
            {questionListGrid}
          </>
        )}
    </Box>
  );
};

export default CategoryAndQuestionForm;
